import { KeyboardEvent, useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface ChipsInputProps {
	values: string[];
	onChange: (values: string[]) => void;
	placeholder?: string;
	chipClassName?: string;
	getSuggestions?: (query: string) => Promise<string[]>;
}

// Shared free-text chip entry: Enter or comma commits a chip, with optional
// async typeahead suggestions (debounced) below the input.
export function ChipsInput({ values, onChange, placeholder, chipClassName, getSuggestions }: ChipsInputProps) {
	const { t } = useTranslation();
	const [inputValue, setInputValue] = useState('');
	const [focused, setFocused] = useState(false);
	const [suggestions, setSuggestions] = useState<string[]>([]);
	const debounceRef = useRef<ReturnType<typeof setTimeout>>();
	const blurRef = useRef<ReturnType<typeof setTimeout>>();

	useEffect(() => () => clearTimeout(blurRef.current), []);

	// Keep typed text on blur (never silently commit it as a chip); the delay
	// lets a suggestion's onMouseDown commit fire before the list is cleared.
	const handleBlur = () => {
		setFocused(false);
		blurRef.current = setTimeout(() => setSuggestions([]), 150);
	};

	useEffect(() => {
		if (!getSuggestions || inputValue.trim().length < 2) {
			setSuggestions([]);
			return;
		}
		clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(async () => {
			try {
				const results = await getSuggestions(inputValue.trim());
				setSuggestions(results.filter(s => !values.includes(s)).slice(0, 10));
			} catch {
				setSuggestions([]);
			}
		}, 300);
		return () => clearTimeout(debounceRef.current);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [inputValue, getSuggestions]);

	const commit = (raw: string) => {
		const value = raw.trim();
		if (value === '' || values.includes(value))
			return;
		onChange([...values, value]);
		setInputValue('');
		setSuggestions([]);
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' || e.key === ',') {
			e.preventDefault();
			commit(inputValue);
		} else if (e.key === 'Backspace' && inputValue === '' && values.length > 0)
			onChange(values.slice(0, -1));
	};

	return (
		<div className="space-y-2">
			<div className="flex flex-wrap gap-1.5">
				{values.map(value => (
					<Badge
						key={value}
						variant="outline"
						className={cn("gap-1 pr-1 font-normal", chipClassName)}
					>
						{value}
						<button
							type="button"
							onClick={() => onChange(values.filter(v => v !== value))}
							className="rounded-sm hover:bg-muted p-0.5"
							aria-label={`Remove ${value}`}
						>
							<X className="h-3 w-3" />
						</button>
					</Badge>
				))}
			</div>
			<Input
				value={inputValue}
				onChange={e => setInputValue(e.target.value)}
				onKeyDown={handleKeyDown}
				onFocus={() => setFocused(true)}
				onBlur={handleBlur}
				placeholder={placeholder}
			/>
			{!focused && inputValue.trim() !== '' && (
				<p className="text-xs text-muted-foreground">
					{t("peopleSearch.chips.pressEnter", "Press Enter to add")}
				</p>
			)}
			{suggestions.length > 0 && (
				<div className="border rounded-sm divide-y">
					{suggestions.map(suggestion => (
						<button
							key={suggestion}
							type="button"
							className="block w-full text-left px-3 py-1.5 text-sm hover:bg-muted"
							onMouseDown={e => { e.preventDefault(); commit(suggestion); }}
						>
							{suggestion}
						</button>
					))}
				</div>
			)}
		</div>
	);
}
